import { useCallback, useEffect, useRef, useState } from 'react';

import { ancanaApi } from '../../../api/ancanaApi';
import { IFloorPlan, IFloorPlanRender } from '../../../types/models/floorPlans';

const RENDERS = 'floor-plan-renders/';
const PLANS = 'floor-plans/';

/**
 * How often to ask whether a generation has finished.
 *
 * A generation takes tens of seconds, so this is not a race — it is a
 * progress bar. Two seconds keeps it feeling live without spending a request
 * per second on a queue that will not have moved.
 */
const POLL_MS = 2000;

/**
 * How much history to pull when the wizard opens.
 *
 * The API pages at three by default, which would make "what did we already
 * try" show the last three attempts and hide the rest. A design accumulates
 * tens of renders over its life, not thousands, so one generous page is the
 * whole history in practice and costs one request.
 */
const PAGE = 100;

const message = (e: any, fallback: string) =>
  e?.response?.data?.detail
  || (typeof e?.response?.data === 'string' ? e.response.data : '')
  || e?.message
  || fallback;

/** Paginated or not — tests and the real API disagree, and neither is wrong. */
const rowsOf = <T, >(data: any): T[] => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

/**
 * Which published version is in force, and whether it is this scope's own.
 *
 * A unit with no override of its own shows the design's plan — the same
 * override-then-design order the API applies in `Unit.floor_plan`. One
 * function for it because `hydrate` has to answer the question the moment the
 * response lands, before the state it would otherwise be derived from has been
 * committed.
 */
const resolveCurrent = (plans: IFloorPlan[], unitId?: number) => {
  const scoped = plans.filter(p => (unitId ? p.unit === unitId : p.unit == null));
  const own = scoped.find(p => p.is_current);
  const designLevel = plans.filter(p => p.unit == null);
  const inherited = unitId ? designLevel.find(p => p.is_current) : undefined;
  return {
    current: own || inherited,
    isInherited: !own && !!inherited,
    // The history panel lists the versions of whichever owner the current plan
    // belongs to. Listing a unit's empty history beside an inherited design
    // plan would read as "no versions" next to a visible one.
    versions: own || !inherited ? scoped : designLevel,
  };
};

/**
 * The renders that belong with `activeId`: its root and everything descended
 * from it, oldest first.
 *
 * A "session" is a chain — one source file, one root render, and every
 * revision made from it — and that is what the filmstrip is showing. Grouping
 * by lineage rather than showing every render for the design keeps two
 * unrelated attempts at the same property from appearing as alternatives to
 * each other, which matters far more now that the history is loaded from the
 * server and can span months rather than one sitting.
 */
const lineageOf = (rows: IFloorPlanRender[], activeId?: number): IFloorPlanRender[] => {
  if (!activeId) return [];
  const byId = new Map(rows.map(r => [r.id, r]));
  if (!byId.has(activeId)) return [];

  // Walk to the root, guarding against a cycle the database should make
  // impossible but which would hang the render pass if it ever existed.
  const rootOf = (row: IFloorPlanRender): number => {
    const seen = new Set<number>();
    let node = row;
    while (node.parent != null && byId.has(node.parent) && !seen.has(node.id)) {
      seen.add(node.id);
      node = byId.get(node.parent) as IFloorPlanRender;
    }
    return node.id;
  };

  const root = rootOf(byId.get(activeId) as IFloorPlanRender);
  return rows
    .filter(r => rootOf(r) === root)
    .sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id - b.id);
};

export interface UseFloorPlanRenders {
  /** Every attempt in the active render's chain, oldest first. The filmstrip. */
  chain: IFloorPlanRender[];
  /** The one on screen. */
  active?: IFloorPlanRender;
  setActiveId: (id: number) => void;
  /** Published versions for this scope, newest first. */
  plans: IFloorPlan[];
  /** The version actually in force — the unit's override, or the design's. */
  currentPlan?: IFloorPlan;
  /** True when `currentPlan` is the design's and this wizard is scoped to a unit. */
  isInherited: boolean;
  /** The first read of plans and renders is still in flight. */
  isLoading: boolean;
  isWorking: boolean;
  error?: string;
  clearError: () => void;
  /**
   * Read what is already saved. Safe to call again; called on every open.
   *
   * Resolves with the version in force, so the caller can decide what to show
   * without waiting a render for the derived state to catch up.
   */
  hydrate: () => Promise<IFloorPlan | undefined>;
  /** Make a known render active, fetching it if the history did not carry it. */
  adopt: (renderId: number) => Promise<IFloorPlanRender | undefined>;
  start: (sourceFileId: string, instruction?: string) => Promise<void>;
  revise: (instruction: string) => Promise<void>;
  reKey: (params: { mode: string; white_point: number; line_point: number }) => Promise<void>;
  publish: (notes?: string) => Promise<IFloorPlan | undefined>;
  /** Publish a finished image as it is: no generation, no keying, no cost. */
  upload: (file: File, notes?: string) => Promise<IFloorPlan | undefined>;
  reset: () => void;
}

/**
 * Everything the wizard knows about one level of a design (or one unit's
 * override of it).
 *
 * The server is the source of truth for all of it — the row exists before
 * `start` returns, and published versions outlive every session — so this hook
 * holds no state the backend could not rebuild. That is what lets the wizard
 * open on what is already saved instead of on an empty first step, and what
 * makes "you can close this window, the work carries on" true rather than
 * merely reassuring.
 */
const useFloorPlanRenders = (designId: number, unitId?: number, levelId?: number): UseFloorPlanRenders => {
  // Every render this hook has seen, from the server or from its own POSTs.
  // `chain` is derived from it; the two cannot drift.
  const [history, setHistory] = useState<IFloorPlanRender[]>([]);
  const [plans, setPlans] = useState<IFloorPlan[]>([]);
  const [activeId, setActiveId] = useState<number>();
  const [isLoading, setIsLoading] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState<string>();

  // Polling has to survive re-renders and must stop when the modal closes, or
  // a closed wizard keeps asking about a render nobody is looking at.
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const mounted = useRef(true);
  useEffect(() => () => {
    mounted.current = false;
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const upsert = useCallback((row: IFloorPlanRender) => {
    setHistory(prev => (prev.some(r => r.id === row.id)
      ? prev.map(r => (r.id === row.id ? row : r))
      : [...prev, row]));
  }, []);

  /**
   * Read the saved state: published versions, then the renders behind them.
   *
   * Plans are fetched for the whole design rather than for the unit alone,
   * because a unit with no override of its own still shows the design's plan —
   * `Unit.floor_plan` resolves override-then-design, and a wizard that only
   * asked about the unit would report "nothing saved" for a unit that visibly
   * has a floor plan everywhere else in the app.
   */
  const hydrate = useCallback(async (): Promise<IFloorPlan | undefined> => {
    setIsLoading(true);
    setError(undefined);
    try {
      // Everything is per storey: a level's versions, its pointer, its renders.
      // Omitted only while the design's levels are still unknown, which on a
      // single-level design is the same answer.
      const level = levelId ? { level: levelId } : {};
      const scope = unitId ? { design: designId, unit: unitId, ...level } : { design: designId, ...level };
      const [planRes, renderRes] = await Promise.all([
        ancanaApi.get(PLANS, { params: { design: designId, ...level, page_size: PAGE } }),
        ancanaApi.get(RENDERS, { params: { ...scope, page_size: PAGE } }),
      ]);
      if (!mounted.current) return undefined;

      const fetchedPlans = rowsOf<IFloorPlan>(planRes.data);
      setPlans(fetchedPlans);
      // Merged, not replaced, and the local row wins. Hydration is slower than
      // a click: an operator who opens the wizard and immediately starts a
      // generation would otherwise have that render dropped on the floor when
      // the fetch that began before it finally lands.
      setHistory(prev => {
        const fetched = rowsOf<IFloorPlanRender>(renderRes.data);
        const local = new Map(prev.map(r => [r.id, r]));
        return [...fetched.filter(r => !local.has(r.id)), ...prev];
      });
      return resolveCurrent(fetchedPlans, unitId).current;
    } catch (e: any) {
      // Non-fatal by design. Failing to read the history is not a reason to
      // withhold the wizard — the operator can still pick a source and
      // generate, which is exactly what they could do before any of this
      // existed.
      if (mounted.current) setError(message(e, 'What is already saved could not be loaded.'));
      return undefined;
    } finally {
      if (mounted.current) setIsLoading(false);
    }
  }, [designId, unitId, levelId]);

  const poll = useCallback((id: number) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      if (!mounted.current) return;
      try {
        const { data } = await ancanaApi.get<IFloorPlanRender>(`${RENDERS}${id}/`);
        if (!mounted.current) return;
        upsert(data);
        if (data.status === 'pending' || data.status === 'running') {
          poll(id);
        } else {
          setIsWorking(false);
          // A failed generation is not a failed request: the row came back
          // fine and carries the reason, which is the thing worth showing.
          if (data.status === 'failed') setError(data.error || 'The generation failed.');
        }
      } catch (e: any) {
        if (!mounted.current) return;
        setIsWorking(false);
        setError(message(e, 'Lost contact with the server while generating.'));
      }
    }, POLL_MS);
  }, [upsert]);

  const begin = useCallback(async (request: () => Promise<{ data: IFloorPlanRender }>) => {
    setIsWorking(true);
    setError(undefined);
    try {
      const { data } = await request();
      upsert(data);
      setActiveId(data.id);
      // Only chase it if it is actually still running. `start` answers pending
      // in practice, but polling a row that already finished would leave
      // `isWorking` true for a whole interval with nothing to wait for — which
      // shows up as a spinner on a button the user is trying to press.
      if (data.status === 'pending' || data.status === 'running') {
        poll(data.id);
      } else {
        setIsWorking(false);
        if (data.status === 'failed') setError(data.error || 'The generation failed.');
      }
    } catch (e: any) {
      setIsWorking(false);
      setError(message(e, 'The generation could not be started.'));
    }
  }, [poll, upsert]);

  /**
   * Continue from a render somebody published months ago.
   *
   * Re-read rather than trusted from the list: the list is one page and the
   * render behind an old version can fall off the end of it, and this is the
   * one path where being wrong means the wizard re-keys or revises a render
   * other than the one the operator pointed at.
   */
  const adopt = useCallback(async (renderId: number) => {
    setIsWorking(true);
    setError(undefined);
    try {
      const { data } = await ancanaApi.get<IFloorPlanRender>(`${RENDERS}${renderId}/`);
      if (!mounted.current) return undefined;
      upsert(data);
      setActiveId(data.id);
      return data;
    } catch (e: any) {
      if (mounted.current) setError(message(e, 'That version could not be reopened.'));
      return undefined;
    } finally {
      if (mounted.current) setIsWorking(false);
    }
  }, [upsert]);

  const start = useCallback(async (sourceFileId: string, instruction = '') => {
    await begin(() => ancanaApi.post(`${RENDERS}start/`, {
      design: designId,
      ...(unitId ? { unit: unitId } : {}),
      // Required by the server on a multi-level design, where the ground floor
      // is not a safe guess.
      ...(levelId ? { level: levelId } : {}),
      source_file_id: sourceFileId,
      instruction,
    }));
  }, [begin, designId, unitId, levelId]);

  const revise = useCallback(async (instruction: string) => {
    if (!activeId) return;
    await begin(() => ancanaApi.post(`${RENDERS}${activeId}/revise/`, { instruction }));
  }, [begin, activeId]);

  const reKey = useCallback(async (params: {
    mode: string; white_point: number; line_point: number;
  }) => {
    if (!activeId) return;
    setIsWorking(true);
    setError(undefined);
    try {
      // Synchronous on the server and well under a second — no polling.
      const { data } = await ancanaApi.post<IFloorPlanRender>(
        `${RENDERS}${activeId}/key/`, params);
      upsert(data);
    } catch (e: any) {
      setError(message(e, 'The background could not be re-cut.'));
    } finally {
      setIsWorking(false);
    }
  }, [activeId, upsert]);

  /**
   * Fold a new version in rather than re-reading the list. Publishing moved the
   * owner's pointer, so every version that was current for this scope no longer
   * is — mirroring that here keeps the history panel from showing two current
   * versions until the next open.
   */
  const foldIn = useCallback((data: IFloorPlan) => {
    setPlans(prev => [data, ...prev.map(p => (p.unit === data.unit && p.level === data.level && p.is_current
      ? { ...p, is_current: false }
      : p))]);
  }, []);

  const publish = useCallback(async (notes = '') => {
    if (!activeId) return undefined;
    setIsWorking(true);
    setError(undefined);
    try {
      const { data } = await ancanaApi.post<IFloorPlan>(`${PLANS}publish/`, {
        render: activeId,
        // Never defaulted, and never sent unless the operator ticked the box in
        // the UI — the server rejects a false or missing value, and this is the
        // client half of that same gate.
        matches_source: true,
        notes,
      });
      foldIn(data);
      return data;
    } catch (e: any) {
      setError(message(e, 'The floor plan could not be published.'));
      return undefined;
    } finally {
      setIsWorking(false);
    }
  }, [activeId, foldIn]);

  const upload = useCallback(async (file: File, notes = '') => {
    setIsWorking(true);
    setError(undefined);
    const form = new FormData();
    form.append('design', String(designId));
    if (unitId) form.append('unit', String(unitId));
    // Same rule as `start`: a multi-level design will not have a level guessed.
    if (levelId) form.append('level', String(levelId));
    form.append('image', file);
    form.append('notes', notes);
    try {
      const { data } = await ancanaApi.post<IFloorPlan>(`${PLANS}upload/`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      foldIn(data);
      return data;
    } catch (e: any) {
      // The serializer answers a bad file under `image`, not `detail`.
      const image = e?.response?.data?.image;
      setError((Array.isArray(image) ? image.join(' ') : image)
        || message(e, 'The image could not be uploaded.'));
      return undefined;
    } finally {
      setIsWorking(false);
    }
  }, [designId, unitId, levelId, foldIn]);

  const reset = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    setHistory([]);
    setPlans([]);
    setActiveId(undefined);
    setIsWorking(false);
    setError(undefined);
  }, []);

  const { current, isInherited, versions } = resolveCurrent(plans, unitId);

  return {
    chain: lineageOf(history, activeId),
    active: history.find(r => r.id === activeId),
    setActiveId,
    plans: versions,
    currentPlan: current,
    isInherited,
    isLoading,
    isWorking,
    error,
    clearError: () => setError(undefined),
    hydrate,
    adopt,
    start,
    revise,
    reKey,
    publish,
    upload,
    reset,
  };
};

export default useFloorPlanRenders;
